import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Send, Trash2, Edit, Image as ImageIcon, X, Download } from 'lucide-react';
import { GALLERY_CATEGORIES } from '../constants/galleryCategories';

// "2026-10-17" -> "Sat, Oct 17". Built from the parts, because
// new Date("2026-10-17") is UTC midnight, which is the evening before here.
function formatGoesOut(dateValue) {
  const [y, m, d] = (dateValue || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function tomorrowValue() {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

const STATUS_STYLES = {
  published: { background: '#d4edda', color: '#155724' },
  scheduled: { background: '#dbeafe', color: '#1e40af' },
  draft: { background: '#f8f9fa', color: '#666' },
};

function Posts({ onLogout }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingPostId, setSendingPostId] = useState(null);
  const [sendAudience, setSendAudience] = useState('all');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    image_urls: [],
    available_for_purchase: false,
    gallery_category: '',
    status: 'draft',
    published_at: '',
    show_calendar_promo: false
  });
  const [sending, setSending] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingFeatured, setUploadingFeatured] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);

  const sendingPost = posts.find((p) => p.id === sendingPostId);
  const sentAudiences = sendingPost?.newsletter_sent_audiences || [];
  const alreadySentSelected = sentAudiences.includes(sendAudience);

  useEffect(() => {
    fetchPosts();
  }, []);

  // Zip of every post as a Markdown file (with photo links and comments)
  const downloadAllPosts = async () => {
    setExporting(true);
    try {
      const response = await axios.get('/posts/export', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      // Named here: the backend is on another domain, so the browser hides its filename header
      link.download = `obx-beach-bum-posts-${new Date().toLocaleDateString('en-CA')}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting posts:', error);
      toast.error('Failed to download posts');
    } finally {
      setExporting(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get('/posts');
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`/posts/${editingPost.id}`, formData);
        toast.success('Post updated successfully');
      } else {
        await axios.post('/posts', formData);
        toast.success('Post created successfully');
      }
      setShowDialog(false);
      setFormData({ title: '', content: '', image_url: '', image_urls: [], available_for_purchase: false, gallery_category: '', status: 'draft', published_at: '', show_calendar_promo: false });
      setEditingPost(null);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    // Format date for input field (YYYY-MM-DD)
    let dateValue = '';
    if (post.published_at) {
      const d = new Date(post.published_at);
      dateValue = d.toISOString().split('T')[0];
    }
    setFormData({
      title: post.title,
      content: post.content,
      image_url: post.image_url || '',
      image_urls: post.image_urls || [],
      available_for_purchase: post.available_for_purchase || false,
      gallery_category: post.gallery_category || '',
      status: post.status || 'draft',
      published_at: dateValue,
      show_calendar_promo: post.show_calendar_promo || false
    });
    setShowDialog(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`/posts/${postId}`);
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleSendNewsletter = async (postId) => {
    setSendingPostId(postId);
    setShowSendDialog(true);
  };

  const confirmSendNewsletter = async () => {
    setSending(sendingPostId);
    try {
      const response = await axios.post('/newsletters/send', {
        post_id: sendingPostId,
        audience: sendAudience
      });
      toast.success(response.data.message);
      fetchPosts();
      setShowSendDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(null);
      setSendingPostId(null);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Enter an email address to send the test to');
      return;
    }
    setSendingTest(true);
    try {
      const response = await axios.post('/newsletters/send/test', {
        post_id: sendingPostId,
        test_email: testEmail.trim()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size: 10MB');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.url) {
        const imageUrl = response.data.url;

        // Insert image markdown at cursor position
        const beforeCursor = formData.content.substring(0, cursorPosition);
        const afterCursor = formData.content.substring(cursorPosition);
        const imageMarkdown = `\n\n![](${imageUrl})\n\n`;
        const newContent = beforeCursor + imageMarkdown + afterCursor;

        setFormData({ ...formData, content: newContent });
        setCursorPosition(cursorPosition + imageMarkdown.length);

        toast.success('Image uploaded and inserted!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleContentChange = (e) => {
    setFormData({ ...formData, content: e.target.value });
    setCursorPosition(e.target.selectionStart);
  };

  const handleFeaturedImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large. Maximum size: 10MB');
      return;
    }

    setUploadingFeatured(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post('/upload/image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.url) {
        setFormData({ ...formData, image_url: response.data.url });
        toast.success('Featured image uploaded!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Image upload failed');
    } finally {
      setUploadingFeatured(false);
      event.target.value = '';
    }
  };

  const removeFeaturedImage = () => {
    setFormData({ ...formData, image_url: '' });
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="posts">
      <div data-testid="posts-page">
        <div className="page-header">
          <h1>Newsletter Posts</h1>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button variant="outline" onClick={downloadAllPosts} disabled={exporting} data-testid="download-posts-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Preparing…' : 'Download all posts'}
          </Button>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid="create-post-btn"
                onClick={() => {
                  setEditingPost(null);
                  setFormData({ title: '', content: '', image_url: '', image_urls: [], available_for_purchase: false, gallery_category: '', status: 'draft' });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} data-testid="post-form">
                <div className="form-group">
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    data-testid="post-title-input"
                    className="form-input"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                {/* Featured Image Section */}
                <div className="form-group">
                  <label>Featured Image</label>
                  <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 10px' }}>
                    This image will be shown as the thumbnail on the homepage and in email previews.
                  </p>
                  
                  {formData.image_url ? (
                    <div style={{ 
                      position: 'relative', 
                      display: 'inline-block',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #ddd'
                    }}>
                      <img 
                        src={formData.image_url} 
                        alt="Featured" 
                        style={{ 
                          maxWidth: '300px', 
                          maxHeight: '200px', 
                          display: 'block',
                          objectFit: 'cover'
                        }} 
                      />
                      <button
                        type="button"
                        onClick={removeFeaturedImage}
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          background: 'rgba(0,0,0,0.7)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        id="featured-image-upload"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFeaturedImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('featured-image-upload').click()}
                        disabled={uploadingFeatured}
                        data-testid="upload-featured-image-btn"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        {uploadingFeatured ? 'Uploading...' : 'Upload Featured Image'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Available for Purchase Toggle */}
                {formData.image_url && (
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.available_for_purchase}
                        onChange={(e) => setFormData({ ...formData, available_for_purchase: e.target.checked })}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                      <span>
                        <strong>Available for Purchase</strong>
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>
                          When enabled, a "Buy This Print" button appears on the featured image and every additional
                          photo on this post, and all of them show up in the Gallery.
                        </p>
                        <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>
                          To leave the button off one photo in the story, put "not for sale" in its brackets:
                          ![not for sale](photo link)
                        </p>
                      </span>
                    </label>

                    {formData.available_for_purchase && (
                      <div style={{ marginTop: '12px', marginLeft: '32px' }}>
                        <label htmlFor="gallery_category" style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                          Gallery Category
                        </label>
                        <select
                          id="gallery_category"
                          className="form-input"
                          value={formData.gallery_category}
                          onChange={(e) => setFormData({ ...formData, gallery_category: e.target.value })}
                          style={{ width: '100%', maxWidth: '320px', padding: '10px' }}
                        >
                          <option value="">Featured Images (default)</option>
                          {GALLERY_CATEGORIES.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Calendar Promo Banner Toggle */}
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.show_calendar_promo}
                      onChange={(e) => setFormData({ ...formData, show_calendar_promo: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span>
                      <strong>Show Calendar Promo Banner</strong>
                      <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 0' }}>
                        When enabled, a banner promoting the current calendar will appear at the bottom of this post, linking to the Calendar shop page.
                      </p>
                    </span>
                  </label>
                </div>

                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <label htmlFor="content">Content</label>
                    <div>
                      <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageUpload}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('image-upload').click()}
                        disabled={uploading}
                        data-testid="upload-image-btn"
                      >
                        <ImageIcon className="w-4 h-4 mr-2" />
                        {uploading ? 'Uploading...' : 'Insert Image'}
                      </Button>
                    </div>
                  </div>
                  <textarea
                    id="content"
                    data-testid="post-content-input"
                    className="form-textarea"
                    value={formData.content}
                    onChange={handleContentChange}
                    onSelect={(e) => setCursorPosition(e.target.selectionStart)}
                    required
                    rows={16}
                    placeholder="Write your story here...&#10;&#10;To insert images: Click 'Insert Image' button. The image will appear at your cursor position as:&#10;&#10;![](https://your-image-url.jpg)&#10;&#10;Keep writing below the image!"
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    💡 <strong>Tip:</strong> Click "Insert Image" to upload and insert photos anywhere in your text. Images appear as ![](url) and will display beautifully in emails and on your website.
                  </small>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500' }}>Status:</label>
                      <select
                        value={formData.status}
                        onChange={(e) => {
                          const status = e.target.value;
                          // Scheduling starts at tomorrow unless a later date is already set.
                          const published_at = status === 'scheduled' && !(formData.published_at > tomorrowValue())
                            ? tomorrowValue()
                            : formData.published_at;
                          setFormData({ ...formData, status, published_at });
                        }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d0d0d0',
                          fontSize: '14px',
                          background: formData.status === 'draft' ? '#fff' : STATUS_STYLES[formData.status]?.background
                        }}
                        data-testid="post-status-select"
                      >
                        <option value="draft">Draft</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500' }}>
                        {formData.status === 'scheduled' ? 'Goes out:' : 'Date:'}
                      </label>
                      <input
                        type="date"
                        required={formData.status === 'scheduled'}
                        min={formData.status === 'scheduled' ? tomorrowValue() : undefined}
                        value={formData.published_at}
                        onChange={(e) => setFormData({ ...formData, published_at: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d0d0d0',
                          fontSize: '14px'
                        }}
                        data-testid="post-date-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-post-btn">
                    {editingPost ? 'Update Post' : 'Create Post'}
                  </Button>
                </div>
                {formData.status === 'scheduled' && formData.published_at && (
                  <p style={{ fontSize: '13px', color: '#1e40af', margin: '10px 0 0' }} data-testid="schedule-hint">
                    Hidden until {formatGoesOut(formData.published_at)} at 6am, then it goes live on the site and out in that morning's 7am email.
                  </p>
                )}
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"></div>
        ) : posts.length === 0 ? (
          <div className="empty-state" data-testid="empty-posts">
            <h3>No posts yet</h3>
            <p>Create your first newsletter post to get started</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table" data-testid="posts-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id} data-testid={`post-row-${post.id}`}>
                    <td>{post.title}</td>
                    <td>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        ...(STATUS_STYLES[post.status] || STATUS_STYLES.draft)
                      }}>
                        {post.status === 'scheduled' ? `Goes out ${formatGoesOut(post.published_at)}` : post.status}
                      </span>
                    </td>
                    <td>{new Date(post.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`edit-post-${post.id}`}
                          onClick={() => handleEdit(post)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`send-post-${post.id}`}
                          onClick={() => handleSendNewsletter(post.id)}
                          disabled={sending === post.id}
                          title="Send as newsletter"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        {post.newsletter_sent_audiences?.length > 0 && (
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            Sent: {post.newsletter_sent_audiences.join(', ')}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`delete-post-${post.id}`}
                          onClick={() => handleDelete(post.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Send Newsletter Dialog */}
        <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Newsletter</DialogTitle>
            </DialogHeader>
            <div style={{ padding: '10px 0' }}>
              <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#666', background: '#f8f8f8', padding: '10px 12px', borderRadius: '6px' }}>
                Publishing doesn't email anyone automatically. Daily-frequency free subscribers get this post bundled into the next morning's digest (7am, along with anything else published since the last one) — use "Free Subscribers Only" below only if you want it sent right now instead of waiting.
              </p>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Choose who should receive this newsletter:
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { value: 'all', label: 'All Subscribers', desc: 'Daily-frequency free subscribers + all paid supporters' },
                  { value: 'paid', label: 'Paid Subscribers Only', desc: 'Monthly + Annual supporters' },
                  { value: 'free', label: 'Free Subscribers Only', desc: 'Daily-frequency, non-paying subscribers (weekly-frequency subscribers get this post in Monday\'s digest instead)' },
                ].map(({ value, label, desc }) => {
                  const alreadySent = sentAudiences.includes(value);
                  return (
                    <label key={value} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '15px',
                      border: sendAudience === value ? '2px solid #1a1a1a' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      cursor: alreadySent ? 'not-allowed' : 'pointer',
                      background: alreadySent ? '#f5f5f5' : (sendAudience === value ? '#f8f9fa' : '#fff'),
                      opacity: alreadySent ? 0.6 : 1
                    }}>
                      <input
                        type="radio"
                        name="audience"
                        value={value}
                        checked={sendAudience === value}
                        onChange={(e) => setSendAudience(e.target.value)}
                        disabled={alreadySent}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <div>
                        <strong>{label}</strong>
                        <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
                          {desc}
                        </p>
                        {alreadySent && (
                          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#b45309' }}>
                            Already sent to this audience
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>

              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#666' }}>
                  Want to see it first? Send yourself a preview — it won't count against the audience above or block the real send.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button variant="outline" onClick={sendTestEmail} disabled={sendingTest}>
                    {sendingTest ? 'Sending...' : 'Send Test'}
                  </Button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={confirmSendNewsletter} disabled={sending || alreadySentSelected}>
                  {sending ? 'Sending...' : 'Send Newsletter'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

export default Posts;